import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { forkJoin, Observable, of as of } from 'rxjs';
import { catchError, map, } from 'rxjs/operators';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { InterventionDbService } from '../../services/intervention_db/intervention-db.service';
import { IInterventionDbInterface } from '../../interfaces/intervention-db-interface';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AnalyticsFilterComponent } from '../../components/analytics-filter/analytics-filter.component';
import { TileComponent } from '../../components/graphs/tile/tile.component';
import { BarChartComponent } from '../../components/graphs/bar-chart/bar-chart.component';
import { environment } from '../../environments/environment';
import { InterventionCardComponent } from '../../components/intervention-card/intervention-card.component';
import { MatDialog } from '@angular/material/dialog';
import { DeviationTableComponent } from '../../components/deviation-table/deviation-table.component';
import { IDeviationSummary } from '../../interfaces/deviation-summary.interface';
import { AdoBugsService } from '../../services/bugs_service/bugs.service';


@Component({
    selector: 'app-intervention-analytics',
    standalone: true,
    imports: [
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        DatePipe,
        MatSelectModule,
        MatButtonModule,
        MatTableModule,
        MatPaginator,
        MatSortModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        AnalyticsFilterComponent,
        TileComponent,
        BarChartComponent,
        DeviationTableComponent
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './intervention-analytics.component.html',
    styleUrl: './intervention-analytics.component.scss',
})
export class InterventionAnalyticsComponent implements AfterViewInit {
    pageSize = 20;
    resultsLength = 0;

    displayedColumns: string[] = ['date', 'description', 'actions', 'customer', 'downtime', 'usecase', 'cart', 'reporter', 'bugId'];
    tableData: MatTableDataSource<IInterventionDbInterface> = new MatTableDataSource<IInterventionDbInterface>;
    deviationSummaryData: IDeviationSummary[] = [];
    deviationSummaryVersions: string[] = [];
    barChartKeys = ['issueGroup', 'bugId', 'customer', 'reporter', 'usecase', 'cart']

    hasBeenFetced = false;
    isLoadingResults = false;
    adoProject = environment.adoApiConfig.project;

    noDataQueryMessage = "No data for current filter. Please run query with other filters."
    noQueryRunsMessage = "Please run a query to get analytics."

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    constructor(
        private interventionDbService: InterventionDbService,
        public dialog: MatDialog,
        public bugsService: AdoBugsService
    ) {
    }

    ngAfterViewInit() {
        this.tableData.paginator = this.paginator;
        this.tableData.sort = this.sort;
        this.tableData.sortingDataAccessor = (item: IInterventionDbInterface, property: string) => {
            switch (property) {
                case 'date': return new Date(item.timestamp).getTime();
                default: return item[property as keyof IInterventionDbInterface] as (number | string);
            }
        };
    }

    openDialog(intervention: IInterventionDbInterface) {
        this.dialog.open(
            InterventionCardComponent, {
            maxWidth: "min(800px, 90vw)",
            minWidth: "400px",
            width: "100%",
            data: {
                ...intervention
            }
        });
    }



    fetchData(filters: FormGroup) {
        this.hasBeenFetced = true;
        this.isLoadingResults = true;


        if (!filters.value.customers?.length) {
            this.isLoadingResults = false;
            // Return an empty array as an observable
            return;
        }
        const selectedCustomers: string[] = filters.value.customers;

        const observables: Observable<IInterventionDbInterface[]>[] = selectedCustomers.map(customer =>
            this.interventionDbService.getInteverventionsByCustomer(
                customer,  // Use each selected customer
                filters.value.startDate!,
                filters.value.endDate!,
                "",
                this.pageSize
            ).pipe(catchError(() => of([])))  // Return an empty array on error
        );

        forkJoin(observables)
            .pipe(
                map(data => {
                    this.isLoadingResults = false;

                    if (data === null) {
                        return [];
                    }
                    return data;
                }),
            )
            .subscribe(data => {
                const bugFilter: string[] = this._getCommaSeperatedStringAsLoweredList(filters.value.bugIds);
                const useCaseFilter: string[] = this._getCommaSeperatedStringAsLoweredList(filters.value.useCases);
                const issueGroupFilter: string[] = this._getCommaSeperatedStringAsLoweredList(filters.value.issueGroups);
                const cartFilters: string[] = this._getCommaSeperatedStringAsLoweredList(filters.value.carts);

                const filteredData = this.filterData(data.flat(), bugFilter, useCaseFilter, issueGroupFilter, cartFilters);
                this.tableData.data = filteredData;
                this.resultsLength = filteredData.length;
                this.deviationSummaryData = this._getDeviationSummariesFromInterventions(filteredData);

                const titleObservables = this.deviationSummaryData.map(item =>
                    this.bugsService.getTitleFromTicketId(item.id)
                );

                forkJoin(titleObservables).subscribe(titles => {
                    this.deviationSummaryData = this.deviationSummaryData.map((item, index) => ({
                        ...item,
                        title: titles[index]
                    }));
                });
            });
    }


    getTotalDowntime(data: IInterventionDbInterface[]) {
        return data.reduce((total, item) => {
            return total + (item.downtime ? item.downtime : 0);
        }, 0);
    }


    filterData(data: IInterventionDbInterface[], bugFilters: string[], useCaseFilters: string[], issueGroupFilters: string[], cartsFilters: string[]): IInterventionDbInterface[] {
        return data.filter(item => {
            const bugMatches = !bugFilters.length || (item.bugId != null && bugFilters.includes(String(item.bugId).toLowerCase()));
            const useCaseMatches = !useCaseFilters.length || (item.usecase != null && useCaseFilters.some(filterValue => item.usecase!.toLowerCase().includes(filterValue.toLowerCase())));
            const issueGroupMatches = !issueGroupFilters.length || (item.issueGroup != null && issueGroupFilters.some(filterValue => item.issueGroup!.toLowerCase().includes(filterValue.toLowerCase())));
            const cartMatches = !cartsFilters.length || (item.cart != null && cartsFilters.some(filterValue => item.cart!.toLowerCase().includes(filterValue.toLowerCase())));
            return bugMatches && useCaseMatches && issueGroupMatches && cartMatches;
        });

    }

    _getCommaSeperatedStringAsLoweredList(commaString: string | undefined | null): string[] {
        if (typeof commaString === 'string' && commaString.length > 0) {
            return commaString.trim().toLowerCase().replace(/\s+/g, '').split(',')
                .filter(item => item.length > 0); // Filter out empty strings
        }
        return [];
    }

    _getDeviationSummariesFromInterventions(interventions: IInterventionDbInterface[]): IDeviationSummary[] {
        const summaryMap: { [key: number]: IDeviationSummary } = {};

        interventions.forEach(incident => {
            if (!incident.bugId) {
                return;
            }

            const summary = this._convertInterventionToDeviationSummary(incident);

            if (summaryMap[summary.id]) {
                summaryMap[summary.id].numberOfReports += 1;
                summaryMap[summary.id].totalDowntime += summary.totalDowntime;
                summaryMap[summary.id].swFoundIn += summaryMap[summary.id].swFoundIn.includes(summary.swFoundIn) ? "" : `, ${summary.swFoundIn}`;
            } else {
                summaryMap[summary.id] = summary;
            }
        });

        return Object.values(summaryMap);
    }

    _convertInterventionToDeviationSummary(intervention: IInterventionDbInterface): IDeviationSummary {
        return {
            id: intervention.bugId ?? -1,
            title: "unknown",
            numberOfReports: 1,
            totalDowntime: intervention.downtime,
            swFoundIn: intervention.swVersion
        }

    }
}
